if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const productVariantId = formData.get('id');

        // Check if we need to add an additional product and wait for it to complete
        this.checkAndAddAdditionalProduct(formData)
          .then(() => {
            if (this.cart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }
            config.body = formData;

            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: productVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: productVariantId,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);

              // Reset the isAddingAdditionalProduct flag after a delay to ensure it's not stuck
              setTimeout(() => {
                const cartDrawerItems = document.querySelector('cart-drawer-items');
                if (cartDrawerItems && cartDrawerItems.isAddingAdditionalProduct) {
                  cartDrawerItems.isAddingAdditionalProduct = false;
                  console.log('Resetting isAddingAdditionalProduct flag after cart drawer render');
                }
              }, 2000);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      checkAndAddAdditionalProduct(formData) {
        // Check if we're in a quick-add-modal
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          console.log('Processing additional product from quick-add-modal');
        }

        // Find the additional product element in the document
        const additionalProductElement = document.querySelector('.additional-product');
        if (!additionalProductElement) {
          console.log('No additional product element found in document');
          return Promise.resolve();
        }

        const standardProductId = additionalProductElement.dataset.product;
        const cooledProductId = additionalProductElement.dataset.productCooled;
        const minimumTotal = parseInt(additionalProductElement.dataset.total);

        if (!standardProductId && !cooledProductId) {
          console.log('No additional product IDs found');
          return Promise.resolve();
        }

        const productPrice = this.getProductPrice();
        const productType = this.dataset.productType;
        console.log('Product Form: productType from dataset:', productType);

        return fetch(`${routes.cart_url}.js`)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
          })
          .then(cart => {
            const currentCartTotal = cart.total_price || 0;
            this.processAdditionalProducts(formData, cart, currentCartTotal, productPrice, minimumTotal, standardProductId, cooledProductId, productType);
            return formData;
          })
          .catch(error => {
            console.error('Error fetching cart data:', error);
            const fallbackCartTotal = parseInt(additionalProductElement.dataset.cartTotal) || 0;
            // Create a dummy cart object for fallback
            const dummyCart = { items: [], total_price: fallbackCartTotal };
            this.processAdditionalProducts(formData, dummyCart, fallbackCartTotal, productPrice, minimumTotal, standardProductId, cooledProductId, productType, true);
            return formData;
          });
      }

      processAdditionalProducts(formData, cart, cartTotal, productPrice, minimumTotal, standardProductId, cooledProductId, productType, isFallback = false) {
        const quantity = parseInt(formData.get('quantity') || 1);
        const totalProductPrice = productPrice * quantity;

        // Projected total including the new product(s)
        const projectedTotal = cartTotal + totalProductPrice;

        const items = cart.items || [];

        // Identify existing items in cart (excluding add-ons)
        const otherItems = items.filter(it => String(it.id) !== String(standardProductId) && String(it.id) !== String(cooledProductId));

        // Check for digital-only cart (if applicable)
        const isProductDigital = this.dataset.requiresShipping === 'false';
        const isCartDigital = otherItems.length === 0 || otherItems.every((it) => !it.requires_shipping);

        if (isProductDigital && isCartDigital) {
          console.log('Skipping additional product: Product is digital and cart is empty or digital-only.');
          return;
        }

        const hasCooledInCart = otherItems.some(it => it.product_type && it.product_type.toLowerCase().trim() === 'cooled');
        const hasOtherInCart = otherItems.some(it => !it.product_type || it.product_type.toLowerCase().trim() !== 'cooled');

        const currentProductType = productType ? productType.toLowerCase().trim() : '';
        const isAddingCooled = currentProductType === 'cooled';
        const isAddingOther = currentProductType !== 'cooled';

        console.log('Product Type Check:', {
          raw: productType,
          normalized: currentProductType,
          isAddingCooled,
          isAddingOther,
          hasCooledInCart,
          hasOtherInCart
        });

        const productsToAdd = [];

        // 1. Cooled Product Logic
        if (cooledProductId) {
          if (isAddingCooled || hasCooledInCart) {
            if (projectedTotal > minimumTotal) {
              if (!this.isAdditionalProductInCart(cooledProductId)) {
                productsToAdd.push(cooledProductId);
              }
            }
          }
        }

        // 2. Standard Product Logic
        if (standardProductId) {
          // "if there are also other products"
          if (isAddingOther || hasOtherInCart) {
            if (projectedTotal > minimumTotal) {
              if (!this.isAdditionalProductInCart(standardProductId)) {
                productsToAdd.push(standardProductId);
              }
            }
          }
        }

        if (productsToAdd.length > 0) {
          console.log(`Adding additional products: ${productsToAdd.join(', ')}`);
          this.updateFormDataWithAdditionalProducts(formData, productsToAdd);

          const cartDrawerItems = document.querySelector('cart-drawer-items');
          if (cartDrawerItems) {
            cartDrawerItems.isAddingAdditionalProduct = true;
            console.log('Setting isAddingAdditionalProduct flag in product-form');
          }
        }
      }

      isAdditionalProductInCart(additionalProductId) {
        // Check if we're in a quick-add-modal
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          console.log('Checking if additional product is in cart from quick-add-modal');
        }

        const cartItems = document.querySelectorAll('.cart-item');
        const isInCart = Array.from(cartItems).some(item =>
          item.dataset.additionalProductId === additionalProductId
        );

        console.log(`Additional product ${additionalProductId} ${isInCart ? 'is' : 'is not'} in cart`);
        return isInCart;
      }

      updateFormDataWithAdditionalProducts(formData, productsToAdd) {
        const mainProductId = formData.get('id');
        const mainProductQuantity = formData.get('quantity') || 1;

        formData.delete('id');
        formData.delete('quantity');
        formData.append('items[0][id]', mainProductId);
        formData.append('items[0][quantity]', mainProductQuantity);

        productsToAdd.forEach((id, index) => {
          formData.append(`items[${index + 1}][id]`, id);
          formData.append(`items[${index + 1}][quantity]`, 1);
        });
      }

      getProductPrice() {
        if (this.submitButton && this.submitButton.dataset.price) {
          console.log('Using price from submit button data-price attribute:', this.submitButton.dataset.price);
          return parseInt(this.submitButton.dataset.price);
        }

        // Check if we're in a quick-add-modal
        const quickAddModal = this.closest('quick-add-modal');
        let priceElement;
        console.log(quickAddModal)
        if (quickAddModal) {
          // If we're in a quick-add-modal, look for the price element within the modal
          console.log('Looking for price element within quick-add-modal');
          priceElement = quickAddModal.querySelector('.price__regular .price-item--regular') ||
            quickAddModal.querySelector('.price__sale .price-item--sale');
        } else {
          // If we're not in a quick-add-modal, look for the price element in the document
          priceElement = document.querySelector('.price__regular .price-item--regular') ||
            document.querySelector('.price__sale .price-item--sale');
        }

        if (priceElement) {
          const priceText = priceElement.textContent.trim();
          // Extract the numeric value from the price text (e.g., "$10.00" -> 1000)
          console.log('Using price from price element:', priceText);
          return parseInt(priceText.replace(/[^0-9]/g, ''));
        }

        // If we can't find the price element, return a default value
        console.warn('Could not find product price element, using default value');
        return 0;
      }
    }
  );
}
